<template>
    <v-container fill-height class="align-start">
        <div class="row no-gutters" width="100%">
            <v-col>
                <loading-overlay :loading="loading || pisLoading"></loading-overlay>
                <transfers-date-filters
                  @filter="loadTransfers"
                  @export="onExport"
                />
                <div class="mt-48"></div>
                <div class="d-flex mb-4">
                    <h2 class="my-0">P2P Swaps</h2>
                    <v-spacer />
                    <v-btn
                        v-if="matchingTransfers.length || postedTransfers.length"
                        color="primary"
                        class="white--text rounded-xl text-none subtitle-1 py-5"
                        href="https://www.trustpilot.com/review/kaoshinetwork.com"
                        target="_blank"
                        rel="noopener"
                    >
                        <v-icon
                            color="white"
                            class="mr-1"
                            small
                        >
                            mdi-star
                        </v-icon>
                        Review us
                    </v-btn>
                </div>
                <v-data-iterator
                    :items="!$store.state.user.can_post_offers ? matchingTransfers : postedTransfers"
                    :items-per-page="pagination.pageSize"
                    :page.sync="pagination.page"
                    :loading="loading"
                    hide-default-footer
                >
                    <template v-slot:item="{ item: transfer }">
                        <v-row class="mb-4 no-gutters">
                            <transfer
                                v-if="!$store.state.user.can_post_offers"
                                :transfer="transfer"
                                :selected-order.sync="selectedOrder"
                                style="width: 100%;"
                                :cancelledCountInLast30Days="cancelledCountInLast30Days"
                                @openMemoDialog="openMemoDialog(transfer)"
                                @open-make-payment="openMakePayment"
                                @validateTransaction="(payload) => validateTransaction(transfer.id, payload)"
                                @loading="loading = true"
                                @loaded="loading = false"
                            />
                            <transfer
                                v-else
                                :transfer="transfer"
                                :isPoster="true"
                                :selected-order.sync="selectedOrder"
                                style="width: 100%;"
                                @openMemoDialog="openMemoDialog(transfer)"
                                @open-make-payment="openMakePayment"
                                @cancelTransfer="cancelTransfer"
                                @openManualPaymentDialog="showManualPaymentDialog = true"
                                @loading="loading = true"
                                @loaded="loading = false"
                            />
                        </v-row>
                    </template>
                    <template v-slot:footer>
                        <v-pagination
                            v-model="pagination.page"
                            :length="pagination.pageCount"
                            :total-visible="7"
                        />
                    </template>
                </v-data-iterator>

                <div class="d-flex mt-24 mb-4">
                    <h2 class="my-0">Third Party Remittance Transactions</h2>
                    <v-spacer />
                    <v-btn
                        v-if="thirdPartyOrders && thirdPartyOrders.length > 0"
                        color="primary"
                        class="white--text rounded-xl text-none subtitle-1 py-5"
                        href="https://www.trustpilot.com/review/kaoshinetwork.com"
                        target="_blank"
                        rel="noopener"
                    >
                        <v-icon
                            color="white"
                            class="mr-1"
                            small
                        >
                            mdi-star
                        </v-icon>
                        Review us
                    </v-btn>
                </div>
                <v-data-iterator
                    v-if="thirdPartyOrders && thirdPartyOrders.length > 0"
                    :items="thirdPartyOrders"
                    :items-per-page="thirdPartyPagination.pageSize"
                    :page.sync="thirdPartyPagination.page"
                    hide-default-footer
                >
                    <template v-slot:item="{ item: transfer }">
                        <v-row class="mb-4 no-gutters">
                            <transfer
                                :transfer="transfer"
                                @open-make-payment="openMakePayment"
                                :selected-order.sync="selectedOrder"
                                style="width: 100%;"
                            ></transfer>
                        </v-row>
                    </template>
                    <template v-slot:footer>
                        <v-pagination
                            v-model="thirdPartyPagination.page"
                            :length="thirdPartyPagination.pageCount"
                            :total-visible="7"
                        />
                    </template>
                </v-data-iterator>
                <transfer-modal
                    ref="transferModal"
                    v-if="showTransferModal"
                    @cancel="showTransferModal = false"
                    v-model="showTransferModal"
                    :is-from-transfers="true"
                    :payment-types="[ selectedOrder.poster_payment_method_type ]"
                    :show-transfer-modal="showTransferModal"
                    :selected-order="selectedOrder"
                ></transfer-modal>
            </v-col>
        </div>

        <memo-dialog
            :transfer.sync="showMemoDialog"
            @loading="loading = true"
            @loaded="loading = false"
        />

        <payWithMonoDialog
            v-if="$store.state.user.can_post_offers"
            :show.sync="showManualPaymentDialog"
            :order="selectedOrder"
            :showCancelButton="false"
            @loading="loading = true"
            @loaded="loading = false"
            @success="onManualPaymentSuccess"
        />
    </v-container>
</template>

<script>
    /* eslint-disable no-inner-declarations */
    let currency = require('../modules/currency.js');
    import PlaidService from '../services/PlaidService';

    import transfer from './transfer.vue';
    import transfersDateFilters from './transfers/components/transfers-date-filter'
    import MySocket from '../services/MySocket';
    import transferModal from './transfers/components/transfer-modal';
    import MemoDialog from './transfers/components/memo-dialog.vue';
    import payWithMonoDialog from './transfers/components/pay-with-mono-dialog';
    import TransactionService from '../services/TransactionService';

    const moment = require('moment');

    export default {
        components: {
            transfer,
            transfersDateFilters: transfersDateFilters,
            transferModal,
            MemoDialog,
            payWithMonoDialog,
        },
        mounted: mounted,
        data: data,
        methods: getMethods(),
        watch: {
            'pagination.page': 'getUserTransfers',
            'thirdPartyPagination.page': 'getThirdPartyOrders',
        },
        beforeDestroy: beforeDestroy,
    }

    function beforeDestroy(){
        const vm = this;
        removeSocketListener();

        function removeSocketListener(){
            MySocket.remove('matchCancelled')
            MySocket.remove('acceptedMatch')
            MySocket.remove('matcherPaymentCompleted')
            MySocket.remove('orderMatched')
            MySocket.remove('orderCancelled');
            MySocket.remove('orderCompleted');
            MySocket.remove('orderRemoved')
            MySocket.remove('matcherPaid');
        }
    }

    async function mounted(){
        const vm = this;
        setupUiElements();
        addTrustpilotWidget();

        try {
            const { data } = await Vue.backendApi().pendingMatches().getCancelledCountInLast30Days();
            Vue.set(this, 'cancelledCountInLast30Days', data.count || 0)
        } catch (err) {
            vm.$swal({
                type: 'error',
                title: 'Error loading cancelled count',
                text: err,
            });
        }

        this.getUserTransfers.apply(this)
        vm.getThirdPartyOrders()
        addSocketListeners();
        vm.loading = true;

        if (this.$route.query.oauth_state_id) {
            this.continuePlaidPayment();
        }

        function addTrustpilotWidget() {
            let trustpilotScript = document.createElement('script')
            trustpilotScript.setAttribute('src', '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js')
            trustpilotScript.async = true;
            document.head.appendChild(trustpilotScript)
        }

        function setupUiElements(){
            vm.$store.commit('pageHeaderClass', 'transfer');
            vm.$store.commit('pageHeaderTitle', vm.pageTitle);
            vm.$store.commit('mobileMenuIsVisible', true);
            vm.$store.commit('closeButtonIsVisible', false);
            vm.$store.commit('hideLogoPart', false);
        }
        function addSocketListeners(){

            MySocket.on('acceptedMatch', (acceptedMatch)=> {
                vm.getUserTransfers.apply(vm)
            })

            MySocket.on('matchCancelled', (matchCancelled)=> {
                vm.getUserTransfers.apply(vm);
            })

            MySocket.on('matcherPaymentCompleted', ()=> {
                vm.getUserTransfers.apply(vm);
            })

            MySocket.on('orderMatched', (order) => {
                if (order.user_id === vm.$store.state.user.id) {
                    const index = vm.postedTransfers.findIndex((item) => item.id === order.id);
                    vm.$set(vm.postedTransfers, index, order);
                }
            });

            MySocket.on('orderCancelled', (order) => {
                if (order.user_id === vm.$store.state.user.id) {
                    const index = vm.postedTransfers.findIndex((item) => item.id === order.id);
                    vm.$set(vm.postedTransfers, index, order);
                }
            });

            MySocket.on('orderCompleted', ()=> {
                vm.getUserTransfers.apply(vm)
            });

            MySocket.on('orderRemoved', ()=> {
                vm.getUserTransfers.apply(vm)
            });

            MySocket.on('matcherPaid', ()=> {
                vm.getUserTransfers.apply(vm)
            });
        }
    }

    function data() {
        return {
            matchingTransfers: [],
            postedTransfers: [],
            thirdPartyOrders: [],
            showTransferModal: false,
            loading: false,
            pisLoading: false,
            stateActive: false,
            stateInactive: false,
            pageTitle: 'Transfers',
            selectedOrder: {},
            filters: {
                end_date: null,
                start_date: null,
                state: null,
            },
            pagination: {
                pageSize: 3,
                page: 1,
                pageCount: null,
            },
            thirdPartyPagination: {
                pageSize: 3,
                page: 1,
                pageCount: null,
            },
            showMemoDialog: null,
            showManualPaymentDialog: false,
            cancelledCountInLast30Days: 0,
        };
    }

    function getMethods() {
        return {
            getUserTransfers,
            getThirdPartyOrders,
            loadTransfers,
            onExport,
            getCurrencyFromCode,
            openMakePayment,
            validateTransaction,
            cancelTransfer,
            continuePlaidPayment,
            openMemoDialog,
            onManualPaymentSuccess,
        };

        function getUserTransfers() {
            const vm = this;
            const params = {
                ...this.pagination,
                ...this.filters,
            };
            if (vm.$store.state.user.can_post_offers) {
                Vue.backendApi().order().listUsersPostedOrders(vm.$store.state.user.id, params).then(success, error);

                function success(response) {
                    vm.loading = false;

                    if (response) {
                        vm.postedTransfers = response.data.orders;
                        vm.$set(vm, 'pagination', response.data.pagination);
                    }
                }

                function error(error) {
                    vm.loading = false;
                    vm.$swal({
                        type: 'error',
                        title: 'Error loading users posted transfers',
                        text: error
                    });
                }

            } else {

                Vue.backendApi().order().listUsersMatchedOrders(vm.$store.state.user.id, params).then(success, error);

                function success(response) {
                    if (response) {
                        vm.loading = false;
                        vm.matchingTransfers = response.data.matches;
                        vm.$set(vm, 'pagination', response.data.pagination);
                    }
                }

                function error(err) {
                    vm.loading = false;
                    vm.$swal({
                        type: 'error',
                        title: 'Error loading users matching transfers',
                        text: err
                    });
                }

            }
        }

        async function getThirdPartyOrders() {
            const params = {
                ...this.thirdPartyPagination,
                ...this.filters,
            };
            try {
                const {
                    data: {
                        orders,
                        pagination,
                    },
                } = await Vue.backendApi()
                    .thirdPartyOrders()
                    .listOrders(params);
                this.thirdPartyOrders = orders;
                this.$set(this, 'thirdPartyPagination', pagination);
            } catch (error) {
                this.$swal({
                    type: 'error',
                    title: 'Error loading third party orders',
                    text: error,
                });
            }
        }

        function loadTransfers(filters) {
            this.loading = true;
            this.filters = filters;
            this.getUserTransfers();
            this.getThirdPartyOrders();
        }

        function onExport(payload) {
            this.loading = true;
            return Vue.backendApi().order().exportTransfers(this.$store.state.user.id, payload)
                .then((response) => {
                    this.loading = false;
                    const { url } = response.data;
                    window.open(url); // eslint-disable-line
                })
                .catch((err) => {
                    this.loading = false;
                    this.$swal({
                        type: 'error',
                        title: 'Error exporting orders',
                        text: err,
                    });
                });
        }

        function getCurrencyFromCode(currencyCode){
            var c = currency.findCurrency(currencyCode);
            c = currency.addCurrencySteps(c);
            c = currency.addFlagLink(c);
            return c;
        }

        function openMakePayment() {
            const vm = this;
            vm.showTransferModal = true;
        }

        async function validateTransaction(matchId, payload) {
          const index = this.matchingTransfers.findIndex((item) => item.id === matchId);
          if(!this.matchingTransfers[index]) {
              return false;
          }
          const match = this.matchingTransfers[index];

            try {
                this.loading = true;
                await TransactionService.validate(this, match, payload)
            } catch (err) {
            } finally {
                this.loading = false;
                this.getUserTransfers();
            }

            if (match.type === 'hybrid') {
                try {
                    this.loading = true;
                    const { data: unifiedTransaction } = await Vue.backendApi()
                        .unified()
                        .getLatestTransaction(matchId);

                    await this.waitForUnifiedPay()

                    window.UnifiedPay.unmount()
                    window.UnifiedPay.mount(null, {
                        transactionId: unifiedTransaction.transaction_id,
                        token: process.env.MIX_UNIFIED_API_TOKEN,
                    });

                    window.UnifiedPay.open()
                } catch (err) {
                    console.log('err', err);
                } finally {
                    this.loading = false;
                }
            }
        }

        function cancelTransfer(transfer){
            const vm = this;
            if (vm.$store.state.user.can_post_offers) {
                vm.$swal({
                    type: 'warning',
                    title: 'Warning',
                    html: '<p>You are about to cancel your transfer! <br /><br /></p> <p><strong>Do you still want to cancel this transfer?</strong></p>',
                    showCancelButton: true,
                    preConfirm: () => {
                        vm.loading = true;
                        Vue.backendApi().order(transfer.id).cancelOrder().then(success, error);
                    }
                });

            }

            function success() {
                vm.loading = false;
                vm.$swal({
                    type: 'success',
                    title: 'Transfer cancelled',
                });
            }

            function error(error) {
                vm.loading = false;
                vm.$swal({
                    type: 'error',
                    title: 'Error rejecting transfer',
                    text: error
                });
            }
        }

        async function continuePlaidPayment() {
            this.pisLoading = true;

            const token = localStorage.getItem('KOSHI_PLAID_PIS_LINK_TOKEN');
            const orderId = localStorage.getItem('KOSHI_PLAID_PIS_ORDER_ID');
            const paymentId = localStorage.getItem('KOSHI_PLAID_PIS_PAYMENT_ID');

            localStorage.removeItem('KOSHI_PLAID_PIS_LINK_TOKEN');
            localStorage.removeItem('KOSHI_PLAID_PIS_ORDER_ID');
            localStorage.removeItem('KOSHI_PLAID_PIS_PAYMENT_ID');

            if (!token || !orderId || !paymentId) {
                this.pisLoading = false;
                this.$router.push({
                    name: 'transfers',
                    query: null,
                });
                return false;
            }

            const onSuccess = (t,metadata) => {
                Vue.backendApi().order(orderId)
                    .completePayment('PLAID', {
                        paymentId,
                    })
                    .then((response) => {
                        this.pisLoading = false;
                        const { state } = response.data;
                        this.$swal({
                            type: 'success',
                            title: 'Payment completed successfully',
                            text: '',
                        });
                    })
                    .catch((error) => {
                        this.pisLoading = false;
                        this.$swal({
                            type: 'error',
                            title: 'Error checking payment status',
                            text: error,
                        });
                    });
            };

            const onExit = (err, metadata) => {
                console.log('Plaid onExit', err, metadata);
                this.pisLoading = false;
                if (err) {
                    this.$swal({
                        type: 'error',
                        title: 'Plaid Error',
                        text: PlaidService.handleError(err),
                    });
                }
            };

            const plaidConfig = {
                token,
                receivedRedirectUri: window.location.href,
                onSuccess,
                onExit,
            };

            const plaidService = new PlaidService();

            try {
                await plaidService.init();
                plaidService.create(plaidConfig);
                await plaidService.open();
            } catch (err) {
                this.pisLoading = false;
                this.$swal({
                    type: 'error',
                    title: 'Plaid error',
                    text: err.message,
                });
            }
        }

        function openMemoDialog(transfer) {
            this.showMemoDialog = transfer;
        }

        async function onManualPaymentSuccess(status) {
            if (status === 'successful') {
                this.$swal({
                    type: 'success',
                    title: 'Payment received',
                });
            }

            if (status === 'processing' || status === 'pending') {
                await this.$swal({
                    type: 'success',
                    title: `Payment is ${status}`,
                });
            }

            this.getUserTransfers();
            this.showManualPaymentDialog = false;
        }
    }
</script>

<style scoped>
    .mt-48 {
        margin-top: 192px !important;
    }
</style>
<style>
    .theme--light.v-pagination .v-pagination__item {
        background: none !important;
        color: #4147d6;
    }

    .v-pagination__item {
        border: none;
        box-shadow: none !important;
        font-style: normal;
        font-size: 14px;
        line-height: 136.5%;
        text-decoration: none;
    }

    .theme--light.v-pagination .v-pagination__item--active {
        font-weight: 800;
    }
</style>
